import { LoadingSpinner } from "@msys/ui";
import {
  List,
  ListItem,
  ListItemAvatar,
  ListItemButton,
  ListItemText,
  Typography,
} from "@mui/material";
import { useTranslate } from "@tolgee/react";
import { type OrganisationType } from "../../clients/graphqlTypes.js";
import { UserAvatar } from "../features/users/UserAvatar.js";

export interface AvailableUser {
  id: string;
  firstname: string;
  familyname: string;
  avatar: { id: string; url: string };
  organisationTitle: string;
  organisationId: string;
  organisationType?: OrganisationType | null;
}

interface Props {
  isLoading: boolean;
  availableUsers: AvailableUser[];
  selectedUser: AvailableUser | null;
  handleSelect: (user: AvailableUser) => void;
  /** A non-null hint shows the user's row disabled, with the hint as its reason. */
  getDisabledHint?: (user: AvailableUser) => string | null;
}

export const SelectUserList = ({
  isLoading,
  availableUsers,
  selectedUser,
  handleSelect,
  getDisabledHint,
}: Props) => {
  const { t } = useTranslate(["Global"]);

  if (isLoading) {
    return <LoadingSpinner />;
  }

  return (
    <List>
      {availableUsers.length > 0 ? (
        availableUsers.map(user => {
          const disabledHint = getDisabledHint?.(user) ?? null;
          return (
            <ListItemButton
              key={user.id}
              onClick={() => {
                if (disabledHint === null) handleSelect(user);
              }}
              selected={user === selectedUser}
              disabled={disabledHint !== null}
            >
              <ListItemAvatar
                sx={theme => ({
                  minWidth: theme.layout.listItemMinWidth.lg,
                })}
              >
                <UserAvatar
                  userAvatar={{
                    __typename: "AvailableUser",
                    ...user,
                    avatar: {
                      __typename: "AttachmentSnapshot",
                      ...user.avatar,
                    },
                  }}
                  size="m"
                />
              </ListItemAvatar>
              <ListItemText
                primary={
                  <Typography variant="h3">{user.organisationTitle}</Typography>
                }
                secondary={disabledHint}
              />
            </ListItemButton>
          );
        })
      ) : (
        <ListItem sx={{ color: theme => theme.palette.secondary.main }}>
          <ListItemText
            primary={t("No organisations available", { ns: "Global" })}
            color="inherit"
          />
        </ListItem>
      )}
    </List>
  );
};
